import React from 'react';
import { useTranslation } from 'react-i18next';
import type { PlayerGraphResultsDto } from '@poker-stats/contracts';
import V2RealChart from './V2RealChart';
import formatHands from '../../utils/formatHands';
import formatWinnings from '../../utils/formatWinnings';
import {
  CHART_COLLAPSED_KEY,
  CHART_POSITION_KEY,
  readChartPrefs,
  type ChartPosition,
} from '../utils/chartPrefs';

type V2ChartSectionProps = {
  hands: number;
  periodLabel: string;
  winnings: number | null | undefined;
  evWinnings: number | null | undefined;
  evBb100: number | null | undefined;
  rlBb100: number | null | undefined;
  graph?: PlayerGraphResultsDto;
  loading?: boolean;
  position: ChartPosition;
  onPositionChange: (position: ChartPosition) => void;
};

const V2ChartSection: React.FC<V2ChartSectionProps> = ({
  hands,
  periodLabel,
  winnings,
  evWinnings,
  evBb100,
  rlBb100,
  graph,
  loading,
  position,
  onPositionChange,
}) => {
  const { t } = useTranslation('translation');
  const [collapsed, setCollapsed] = React.useState(() => readChartPrefs().collapsed);
  const detailsRef = React.useRef<HTMLDetailsElement>(null);

  React.useEffect(() => {
    try {
      localStorage.setItem(CHART_COLLAPSED_KEY, collapsed ? '1' : '0');
    } catch {
      // ignore storage errors
    }
  }, [collapsed]);

  const handleToggle = (event: React.SyntheticEvent<HTMLDetailsElement>) => {
    setCollapsed(!event.currentTarget.open);
  };

  const handleMove = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    const nextPosition = position === 'top' ? 'bottom' : 'top';
    onPositionChange(nextPosition);
    try {
      localStorage.setItem(CHART_POSITION_KEY, nextPosition);
    } catch {
      // ignore storage errors
    }
  };

  const winningsLabel = winnings == null ? '—' : formatWinnings(winnings, 1, true);
  const evLabel = evWinnings == null ? '—' : formatWinnings(evWinnings, 1, true);

  return (
    <details ref={detailsRef} className="chart-section" open={!collapsed} onToggle={handleToggle}>
      <summary className="chart-summary">
        <div>
          <h2 id="chart-title">Динамика результатов</h2>
          <p className="chart-subtitle">
            {formatHands(hands, 0)} раздач · {periodLabel}
            {collapsed ? (
              <>
                {' '}
                ·{' '}
                <span className={winnings == null ? '' : winnings >= 0 ? 'positive' : 'negative'}>
                  {winningsLabel}
                </span>
                {evWinnings != null ? (
                  <>
                    {' '}
                    · EV{' '}
                    <span className={evWinnings >= 0 ? 'positive' : 'negative'}>{evLabel}</span>
                  </>
                ) : null}
                {evBb100 != null ? (
                  <>
                    {' '}
                    · EV BB{' '}
                    <span className={evBb100 >= 0 ? 'positive' : 'negative'}>
                      {evBb100}
                      <small> bb/100</small>
                    </span>
                  </>
                ) : null}
                {rlBb100 != null ? (
                  <>
                    {' '}
                    <span className="chart-subtitle-metric">
                      · <span title={t('red_line')}>RL</span>{' '}
                      <span className={rlBb100 >= 0 ? 'positive' : 'negative'}>
                        {rlBb100}
                        <small> bb/100</small>
                      </span>
                    </span>
                  </>
                ) : null}
              </>
            ) : null}
          </p>
        </div>
        <div className="chart-summary-actions">
          <div className="chart-legend" aria-hidden={collapsed}>
            <span>
              <i className="profit"></i>Profit
            </span>
            <span>
              <i className="showdown"></i>Showdown
            </span>
            <span>
              <i className="no-showdown"></i>Non-showdown
            </span>
            <span>
              <i className="ev"></i>EV
            </span>
          </div>
          <button
            type="button"
            className="outline-button chart-move-btn"
            onClick={handleMove}
            aria-label={position === 'top' ? 'Перенести график вниз' : 'Перенести график вверх'}
          >
            {position === 'top' ? '↓ Вниз' : '↑ Вверх'}
          </button>
        </div>
      </summary>
      <div className="chart-frame" tabIndex={0}>
        <V2RealChart
          graph={graph}
          loading={loading}
          handsLabel={`${formatHands(hands, 0)} раздач`}
          winningsLabel={winningsLabel}
          evLabel={evLabel}
        />
      </div>
      <p className="chart-caption">Динамика по выбранным фильтрам.</p>
    </details>
  );
};

export default V2ChartSection;
